import { CreativeType, TCreativeAsset } from './types';

export const getCreativeType = (
  creativeAssets: TCreativeAsset[]
): CreativeType => {
  return creativeAssets.some(asset => asset.asset_type === 'image')
    ? 'banner'
    : 'text link';
};

// Display-name overrides for asset keys whose raw name would mislead
// publishers: tile_button_text is surfaced as the advertiser's requested CTA.
const ASSET_DISPLAY_NAMES: Record<string, string> = {
  tile_button_text: 'Call to Action',
};

export const formatAssetName = (name: string): string =>
  ASSET_DISPLAY_NAMES[name.toLowerCase()] ??
  name.replace(/_/g, ' ').replace(/^./, c => c.toUpperCase());

// Note shown under the CTA asset in creative views, explaining why it's there.
export const CTA_ASSET_NOTE =
  'The advertiser requests this call-to-action to appear in your content — ' +
  'use it as the text of the button or link that sends readers to the offer.';

export const isCtaAsset = (name: string): boolean =>
  name.toLowerCase() === 'tile_button_text';

// Preferred display order for creative text assets shown to publishers.
// Anything not listed trails the prioritized ones, alphabetically by name.
const TEXT_ASSET_PRIORITY = [
  'title',
  'tile_button_text',
  'promo_code',
  'offer_details',
  'steps_to_redeem',
  'terms_and_conditions',
];

export const sortCreativeTextAssets = <T extends { name: string }>(
  assets: T[]
): T[] =>
  [...assets].sort((a, b) => {
    const ia = TEXT_ASSET_PRIORITY.indexOf(a.name.toLowerCase());
    const ib = TEXT_ASSET_PRIORITY.indexOf(b.name.toLowerCase());
    const ra = ia === -1 ? Number.MAX_SAFE_INTEGER : ia;
    const rb = ib === -1 ? Number.MAX_SAFE_INTEGER : ib;
    if (ra !== rb) return ra - rb;
    return a.name.localeCompare(b.name);
  });

export const stripHtml = (html: string): string => {
  const withBreaks = html
    .replace(/<\/p>/gi, '\n')
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<\/li>/gi, '\n')
    .replace(/<\/h[1-6]>/gi, '\n')
    .replace(/<\/blockquote>/gi, '\n');
  const doc = new DOMParser().parseFromString(withBreaks, 'text/html');
  return (doc.body.textContent ?? '').replace(/\n{3,}/g, '\n\n').trim();
};
